import Tooltip, { TooltipProps } from "@mui/material/Tooltip";
import {
  cloneElement,
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export type OverflowTooltipProps = Omit<TooltipProps, "children" | "title"> & {
  children: ReactElement;
  title: NonNullable<TooltipProps["title"]>;
};

const assignRef = <T,>(ref: Ref<T> | undefined, value: T | null) => {
  if (!ref) {
    return;
  }

  if (typeof ref === "function") {
    ref(value);
    return;
  }

  (ref as React.MutableRefObject<T | null>).current = value;
};

export const OverflowTooltip = ({
  children,
  title,
  ...tooltipProps
}: OverflowTooltipProps) => {
  const elementRef = useRef<HTMLElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateOverflow = useCallback(() => {
    const element = elementRef.current;
    const nextIsOverflowing = Boolean(
      element && element.scrollWidth > element.clientWidth,
    );

    setIsOverflowing((currentIsOverflowing) =>
      currentIsOverflowing === nextIsOverflowing
        ? currentIsOverflowing
        : nextIsOverflowing,
    );
  }, []);

  const childRef = (children as ReactElement & { ref?: Ref<HTMLElement> }).ref;
  const setChildRef = useCallback(
    (element: HTMLElement | null) => {
      elementRef.current = element;
      assignRef(childRef, element);
    },
    [childRef],
  );

  useEffect(() => {
    updateOverflow();

    const element = elementRef.current;
    if (!element) {
      return undefined;
    }

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(updateOverflow);

    resizeObserver?.observe(element);
    window.addEventListener("resize", updateOverflow);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateOverflow);
    };
  }, [children, title, updateOverflow]);

  const child = cloneElement(children, {
    ref: setChildRef,
  } as Record<string, unknown>);

  if (!isOverflowing) {
    return child;
  }

  return (
    <Tooltip {...tooltipProps} title={title}>
      {child}
    </Tooltip>
  );
};
